import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Alert, Button, Col, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import { BaseProps } from "../../../models/auth";
import { IValuationVariable, create, deleteOne, list, update, valuationVariableSchema } from "../../../models/valuationVariable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faPencil, faPlus, faSave, faStop, faTimes } from "@fortawesome/free-solid-svg-icons";
import { OrbitProgress } from "react-loading-indicators";

interface ValuationVariablePageProps extends BaseProps {}

export const ValuationVariablePage = (props: ValuationVariablePageProps) => {
  const [variables, setVariables] = useState<IValuationVariable[]>([]);
  const [variable, setVariable] = useState<IValuationVariable>();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [loading, setIsLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<IValuationVariable>({});
  useEffect(() => {
    if (props.profile?.id) {
      list().then((response) => {
        setVariables(response.data?.valuationVariables);
        setIsLoading(false);
      });
    }
  }, [props.profile]);

  if (!props.profile?.isAdmin) {
    return <Alert color="danger">Unauthorized!!!</Alert>;
  }
  if (loading) {
    return (
      <div className="spinner-center">
        <OrbitProgress variant="track-disc" color={"#319dcc"} speedPlus={0} easing="linear" />
      </div>
    );
  }

  return (
    <>
      <Modal isOpen={modalIsOpen} toggle={(x) => setModalIsOpen(x != x)}>
        <ModalHeader>Manage Valuation Variable</ModalHeader>
        <ModalBody>
          <Row>
            <Col>
              <Label for="txtName">Name</Label>
              {errors?.name && (
                <>
                  <br />
                  <span className="text-danger">{errors.name}</span>
                </>
              )}
              <Input
                id="txtName"
                value={variable?.name}
                onChange={(e) => {
                  e.persist();
                  setVariable((x) => ({ ...x, name: e.target.value }));
                }}
              ></Input>
              <Label for="ddlType">Type</Label>
              {errors?.type && (
                <>
                  <br />
                  <span className="text-danger">{errors.type}</span>
                </>
              )}
              <select
                className="form-control"
                id="ddlCategory"
                value={variable?.type}
                onChange={(e) => {
                  e.persist();
                  setVariable((x) => ({ ...x, type: e.target.value }));
                }}
              >
                <option value={""}>Select...</option>
                <option value={"Simple"}>Simple</option>
                <option value={"Number"}>Number</option>
                <option value={"Currency"}>Currency</option>
              </select>
              <Label for="txtMask">Mask</Label>
              {errors?.mask && (
                <>
                  <br />
                  <span className="text-danger">{errors.mask}</span>
                </>
              )}
              <Input
                id="txtMask"
                value={variable?.mask}
                onChange={(e) => {
                  e.persist();
                  setVariable((x) => ({ ...x, mask: e.target.value }));
                }}
              ></Input>
              <Label for="txtPlaceholder">Placeholder</Label>
              {errors?.placeholder && (
                <>
                  <br />
                  <span className="text-danger">{errors.placeholder}</span>
                </>
              )}
              <Input
                id="txtPlaceholder"
                value={variable?.placeholder}
                onChange={(e) => {
                  e.persist();
                  setVariable((x) => ({ ...x, placeholder: e.target.value }));
                }}
              ></Input>
              <Label for="txtSortOrder">Sort order</Label>
              {errors?.sortOrder && (
                <>
                  <br />
                  <span className="text-danger">{errors.sortOrder}</span>
                </>
              )}
              <Input
                type="number"
                id="txtSortOrder"
                value={variable?.sortOrder}
                onChange={(e) => {
                  e.persist();
                  setVariable((x) => ({ ...x, sortOrder: e.target.value ? parseInt(e.target.value) : null }));
                }}
              ></Input>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={async () => {
              try {
                await valuationVariableSchema.validateSync(variable, {
                  abortEarly: false,
                });
                if (variable?.id) {
                  await update(variable);
                } else {
                  await create(variable);
                }
                window.location.reload();
              } catch (yupErrors) {
                let errors: IValuationVariable = {};
                yupErrors.inner.map((x: any) => {
                  errors[x.path] = x.message;
                });
                setErrors(errors);
              }
            }}
          >
            <FontAwesomeIcon icon={faSave} className="mr-2"></FontAwesomeIcon>
            Save
          </Button>
          <Button color="secondary" onClick={() => setModalIsOpen(false)}>
            <FontAwesomeIcon icon={faStop} className="mr-2"></FontAwesomeIcon>
            Cancel
          </Button>
          {variable?.id && (
            <Button
              color="danger"
              onClick={() => {
                deleteOne(variable);
                window.location.reload();
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2"></FontAwesomeIcon>
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>
      <Helmet>
        <meta charSet="utf-8"></meta>
        <title>Valuation Variables | Asset manager</title>
      </Helmet>

      <Container className="mt-5">
        <h1>
          <FontAwesomeIcon icon={faCog} className="mr-2"></FontAwesomeIcon> Valuation Variables
        </h1>
        <hr />
        <Button
          color="primary"
          className="mb-2"
          onClick={() => {
            setErrors({});
            setVariable({ name: null });
            setModalIsOpen(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2"></FontAwesomeIcon>Add a new Valuation Variable
        </Button>

        <Table striped responsive>
          <colgroup>
            <col className="col-md-4"></col>
            <col className="col-md-1"></col>
            <col className="col-md-2"></col>
            <col className="col-md-2"></col>
            <col className="col-md-1"></col>
            <col className="col-2"></col>
          </colgroup>
          <thead>
            <tr className="bg-dark text-light">
              <th>Name</th>
              <th>Type</th>
              <th>Mask</th>
              <th>Placeholder</th>
              <th>Order</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {variables?.map((x, i) => {
              return (
                <tr key={i}>
                  <td>{x.name}</td>
                  <td>{x.type}</td>
                  <td>{x.mask}</td>
                  <td>{x.placeholder}</td>
                  <td>{x.sortOrder}</td>
                  <td>
                    <Button
                      color="primary"
                      onClick={() => {
                        setErrors({});
                        setVariable(x);
                        setModalIsOpen(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPencil} className="mr-1"></FontAwesomeIcon>
                      Manage
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>
    </>
  );
};
